import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Users, AlertTriangle, TrendingUp, TrendingDown, Minus, Sparkles, RefreshCw, Plus, Trash2, X,
} from "lucide-react";
import CopyButton from "@/components/CopyButton";
import ContextualImportButton from "@/components/ContextualImportButton";
import ContextButton from "@/components/ContextButton";
import DataStateTrendBadge from "@/components/DataStateTrendBadge";
import SourceCalcBtn from "@/components/SourceCalcBtn";
import { useLocalContext, describeScope } from "@/lib/moduleContext";
import { useActiveDoctrine, doctrinePayload } from "@/lib/doctrine";
import { useDemoMode } from "@/lib/demoMode";
import { notify, fmtError } from "@/lib/notify";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";

const RH_IMPORT_FIELDS = [
  { key: "poste", label: "Poste", required: true },
  { key: "departement", label: "Département", default: "" },
  { key: "owner", label: "Owner", default: "vacant" },
  { key: "criticite", label: "Criticité", default: "moyenne" },
  { key: "succession", label: "Plan de succession", default: "absente" },
];

const SUCC_COLOR = {
  "absente": "#E24B4A",
  "en cours": "#EF9F27",
  "préparée": "#1D9E75",
};

const CRIT_COLOR = {
  "critique": "#E24B4A",
  "haute": "#EF9F27",
  "moyenne": "#378ADD",
};

function trendIcon(t) {
  if (t === "haussiere") return <TrendingUp className="w-3 h-3 text-[#E24B4A]" />;
  if (t === "baissiere") return <TrendingDown className="w-3 h-3 text-[#1D9E75]" />;
  return <Minus className="w-3 h-3 text-zinc-500" />;
}

export default function RH() {
  const isDemo = useDemoMode();
  const endpoint = isDemo ? "/hr/summary" : "/workspace/hr/summary";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [commentary, setCommentary] = useState(null);
  const [loadingC, setLoadingC] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [scope] = useLocalContext("rh");
  const activeDoctrine = useActiveDoctrine();
  const [form, setForm] = useState({ poste: "", owner: "", departement: "", criticite: "haute", succession: "absente" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(endpoint).then((r) => setData(r.data)).catch((e) => setError(e?.response?.data?.detail || "Erreur"));

  useEffect(() => {
    setData(null); setError(null);
    load();
    // eslint-disable-next-line
  }, [isDemo]);

  const generate = async () => {
    setLoadingC(true);
    try {
      const r = await api.post("/hr/commentary", { context_hint: describeScope(scope), doctrine: doctrinePayload(activeDoctrine) });
      setCommentary(r.data.commentary);
    } catch (e) {
      setCommentary("Désolé, le commentaire n'a pas pu être généré.");
    } finally {
      setLoadingC(false);
    }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/workspace/hr/postes", form);
      setShowAdd(false);
      setForm({ poste: "", owner: "", departement: "", criticite: "haute", succession: "absente" });
      await load();
    } catch (err) {
      notify.error(fmtError(err, "Erreur"));
    } finally {
      setBusy(false);
    }
  };

  const removePoste = async (id) => {
    if (!window.confirm("Supprimer ce poste ?")) return;
    try {
      await api.delete(`/workspace/hr/postes/${id}`);
      await load();
    } catch (err) {
      notify.error(fmtError(err, "Erreur"));
    }
  };

  if (error) return <div className="text-[#E24B4A] text-sm" data-testid="rh-error">{error}</div>;
  if (!data) return <div className="text-zinc-400">Chargement…</div>;

  // Workspace empty state
  if (!isDemo && data.is_empty) {
    return (
      <div className="space-y-6" data-testid="rh-root">
      <ThreeQPageHeader endpoint="/workspace/rh/top-priority" />
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 text-[#7F77DD] noam-overline">
              <Users className="w-3 h-3" /> Tactique · Ressources humaines
            </div>
            <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">RH décisionnel</h1>
            <p className="text-zinc-400 text-sm mt-2 max-w-3xl">
              Cartographiez vos postes critiques pour piloter votre capital humain. Le score RH se calcule à partir de la couverture en succession.
            </p>
          </div>
          <button onClick={() => setShowAdd(true)} className="btn-primary" data-testid="rh-add-btn">
            <Plus className="w-4 h-4" /> Ajouter un poste critique
          </button>
        </div>
        <div className="noam-card p-12 text-center">
          <Users className="w-12 h-12 text-[#7F77DD] mx-auto opacity-60" />
          <div className="noam-display text-xl mt-4">Votre module RH est vide</div>
          <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
            Aucun poste critique n'est encore enregistré. Commencez par ajouter vos postes-clés (DPO, Lead Cyber, Architecte data…) pour suivre la couverture en succession.
          </p>
          <button onClick={() => setShowAdd(true)} className="btn-primary mt-6 mx-auto inline-flex" data-testid="rh-empty-add">
            <Plus className="w-4 h-4" /> Ajouter votre premier poste
          </button>
        </div>
        <AddPosteModal open={showAdd} onClose={() => setShowAdd(false)} form={form} setForm={setForm} onSubmit={submitAdd} busy={busy} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rh-root">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 text-[#7F77DD] noam-overline">
            <Users className="w-3 h-3" /> Tactique · Ressources humaines
          </div>
          <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">RH décisionnel</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-3xl">
            Le capital humain comme actif décisionnel — postes critiques, succession et indicateurs.
          </p>
        </div>
        {!isDemo && (
          <div className="flex items-center gap-2">
            <ContextButton moduleKey="rh" />
            <ContextualImportButton moduleKey="rh" targetFields={RH_IMPORT_FIELDS} onImported={() => window.location.reload()} />
            <button onClick={() => setShowAdd(true)} className="btn-primary" data-testid="rh-add-btn">
              <Plus className="w-4 h-4" /> Ajouter un poste
            </button>
          </div>
        )}
      </div>

      {/* Headline */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {data.headcount != null && (
          <div className="noam-card p-5" data-testid="rh-headcount">
            <div className="text-xs text-zinc-400">Effectif total</div>
            <div className="noam-display text-3xl font-bold mt-3">{data.headcount}</div>
            <div className="text-[11px] text-zinc-500 mt-1">collaborateurs</div>
          </div>
        )}
        <div className="noam-card p-5 relative" data-testid="rh-score">
          <SourceCalcBtn kpiKey="rh_score" />
          <div className="text-xs text-zinc-400">Score RH consolidé</div>
          <div className="flex items-end justify-between mt-3 gap-2">
            <div className="noam-display text-3xl font-bold" style={{ color: data.score_rh >= 70 ? "#1D9E75" : data.score_rh >= 50 ? "#EF9F27" : "#E24B4A" }}>
              {data.score_rh}<span className="text-base text-zinc-500">/100</span>
            </div>
            <DataStateTrendBadge
              data={data.score_rh}
              etat={data.score_rh >= 70 ? "bon" : data.score_rh >= 50 ? "attention" : "critique"}
              tendance="stable"
              polarity="positive"
              compact
            />
          </div>
          <div className="h-1.5 mt-3 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full" style={{ width: `${data.score_rh}%`, background: data.score_rh >= 70 ? "#1D9E75" : data.score_rh >= 50 ? "#EF9F27" : "#E24B4A" }} />
          </div>
        </div>
        <div className="noam-card p-5 relative" data-testid="rh-postes-critiques">
          <SourceCalcBtn kpiKey="rh_postes_critiques" />
          <div className="text-xs text-zinc-400 flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3 text-[#E24B4A]" /> Postes critiques sans succession
          </div>
          <div className="noam-display text-3xl font-bold mt-3 text-[#E24B4A]">{data.postes_sans_succession}</div>
          <div className="text-[11px] text-zinc-500 mt-1">sur {data.postes_critiques.length} postes-clés</div>
        </div>
        {data.couverture && (
          <div className="noam-card p-5" data-testid="rh-couverture">
            <div className="text-xs text-zinc-400">Couverture postes clés</div>
            <div className="flex items-end justify-between mt-3">
              <div className="noam-display text-3xl font-bold">{data.couverture.value}<span className="text-base text-zinc-500">%</span></div>
              <div className="text-xs flex items-center gap-1 text-[#E24B4A]">{trendIcon(data.couverture.trend)} {data.couverture.delta}%</div>
            </div>
            <div className="text-[11px] text-zinc-500 mt-1">Seuil : {data.couverture.seuil}%</div>
          </div>
        )}
      </div>

      {/* Indicateurs (demo only) */}
      {data.indicateurs && (
        <div className="noam-card p-5">
          <div className="noam-overline mb-3">Indicateurs RH</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.indicateurs.map((i) => {
              const breached = i.unit === "%" && (i.key === "absenteisme" || i.key === "turnover" ? i.value > i.seuil : i.value < i.seuil);
              const c = breached ? "#E24B4A" : "#1D9E75";
              return (
                <div key={i.key} className="border border-white/5 rounded-lg p-4" data-testid={`rh-ind-${i.key}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-zinc-400">{i.label}</span>
                    {trendIcon(i.trend)}
                  </div>
                  <div className="flex items-baseline gap-1 mt-2">
                    <span className="noam-display text-2xl font-bold" style={{ color: c }}>{i.value}</span>
                    <span className="text-xs text-zinc-500">{i.unit}</span>
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-1">Seuil {i.seuil}{i.unit}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Postes critiques table */}
      <div className="noam-card overflow-hidden" data-testid="rh-postes-table">
        <div className="px-5 py-4 border-b border-white/5">
          <div className="noam-overline">Postes critiques</div>
          <div className="noam-display text-base font-semibold">Capital humain stratégique</div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-xs text-zinc-500 border-b border-white/5 bg-white/[0.02]">
            <tr>
              <th className="text-left py-2.5 px-5 font-normal">Poste</th>
              <th className="text-left font-normal">Owner</th>
              <th className="text-left font-normal hidden md:table-cell">Département</th>
              <th className="text-center font-normal">Criticité</th>
              <th className="text-center font-normal">Succession</th>
              {!isDemo && <th className="text-center font-normal w-12"></th>}
            </tr>
          </thead>
          <tbody>
            {data.postes_critiques.map((p) => (
              <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="py-3 px-5 text-white">{p.poste}</td>
                <td className="text-zinc-400">{p.owner || "—"}</td>
                <td className="text-zinc-400 hidden md:table-cell">{p.departement || "—"}</td>
                <td className="text-center">
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-medium capitalize"
                    style={{ background: `${CRIT_COLOR[p.criticite]}1A`, color: CRIT_COLOR[p.criticite] }}>
                    {p.criticite}
                  </span>
                </td>
                <td className="text-center">
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-medium capitalize"
                    style={{ background: `${SUCC_COLOR[p.succession]}1A`, color: SUCC_COLOR[p.succession] }}>
                    {p.succession}
                  </span>
                </td>
                {!isDemo && (
                  <td className="text-center">
                    <button onClick={() => removePoste(p.id)} className="text-zinc-500 hover:text-[#E24B4A] p-1" data-testid={`rh-del-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* AI commentary (demo only — workspace AI commentary will plug into real data later) */}
      {isDemo && (
        <div className="noam-card p-6" data-testid="rh-commentary">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#378ADD]" />
              <div>
                <div className="noam-overline">Lecture IA · Santé RH</div>
                <div className="noam-display text-base font-semibold">Analyse Noam</div>
              </div>
            </div>
            <button onClick={generate} disabled={loadingC} className="btn-primary text-xs disabled:opacity-50" data-testid="rh-generate">
              <RefreshCw className={`w-3.5 h-3.5 ${loadingC ? "animate-spin" : ""}`} />
              {commentary ? "Régénérer" : "Générer le commentaire"}
            </button>
          </div>
          {!commentary && !loadingC && (
            <p className="text-zinc-400 text-sm">Cliquez sur « Générer » pour obtenir une lecture narrative de votre santé RH.</p>
          )}
          {loadingC && (
            <div className="flex items-center gap-2 text-zinc-400 text-sm">
              <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
              <span className="ml-2">Noam analyse…</span>
            </div>
          )}
          {commentary && (
            <>
              <p className="text-zinc-200 leading-relaxed border-l-2 border-[#378ADD] pl-4 italic" data-testid="rh-commentary-text">{commentary}</p>
              <div className="mt-4"><CopyButton text={commentary} label="Copier le commentaire" /></div>
            </>
          )}
        </div>
      )}

      <AddPosteModal open={showAdd} onClose={() => setShowAdd(false)} form={form} setForm={setForm} onSubmit={submitAdd} busy={busy} />
    </div>
  );
}

function AddPosteModal({ open, onClose, form, setForm, onSubmit, busy }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="noam-card w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="rh-add-modal">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="noam-overline">RH</div>
            <div className="noam-display text-lg font-semibold">Nouveau poste critique</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Intitulé du poste</label>
            <input required value={form.poste} onChange={(e) => setForm({ ...form, poste: e.target.value })} placeholder="Ex : Directeur des risques" className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none" data-testid="rh-form-poste" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Owner</label>
              <input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} placeholder="M. Dupont" className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none" data-testid="rh-form-owner" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Département</label>
              <input value={form.departement} onChange={(e) => setForm({ ...form, departement: e.target.value })} placeholder="Direction" className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none" data-testid="rh-form-dept" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Criticité</label>
              <select value={form.criticite} onChange={(e) => setForm({ ...form, criticite: e.target.value })} className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="rh-form-crit">
                <option value="critique">Critique</option>
                <option value="haute">Haute</option>
                <option value="moyenne">Moyenne</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Succession</label>
              <select value={form.succession} onChange={(e) => setForm({ ...form, succession: e.target.value })} className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="rh-form-succ">
                <option value="absente">Absente</option>
                <option value="en cours">En cours</option>
                <option value="préparée">Préparée</option>
              </select>
            </div>
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-50" data-testid="rh-form-submit">
            {busy ? "Enregistrement…" : "Ajouter le poste"}
          </button>
        </form>
      </div>
    </div>
  );
}
